import { useEffect, useState, useCallback } from "react";
import { Box, Theme, Typography, useTheme } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import type { AxisValueFormatterContext } from "@mui/x-charts/models";
import ChartContainer from "../common/ChartContainer";
import Badge from "../ui/badge/Badge";
import { ArrowUpIcon, ArrowDownIcon } from "../../icons";
import { useApi } from "../../hooks/useApi";
import { Currency, DashboardTab } from "../../pages/Dashboard";
import { formatCurrencyAdaptive } from "../../utils/formatCurrency";
import { formatAxisDate } from "../../utils/chartDate";

interface ChartPayload {
  xAxis: string[];
  series: Array<{
    label: string;
    data: number[];
  }>;
}

interface ChartResponse {
  chart: ChartPayload;
  summary: {
    total: number;
    avgPerDay: number;
    totalRefundedAmount: number;
    previousTotal: number;
  };
}

interface AlertsMetricsProps {
  startDate: string;
  endDate: string;
  companyId: number | null;
  brandId?: number | null;
  currency: Currency;
  showTitle?: boolean;
  activeTab?: DashboardTab;
}

export default function AlertsMetrics({
  startDate,
  endDate,
  companyId,
  brandId,
  currency,
  showTitle = true,
  activeTab = "overall",
}: AlertsMetricsProps) {
  const { get, loading } = useApi();
  const [ethocaData, setEthocaData] = useState<ChartResponse | null>(null);
  const [cdrnData, setCdrnData] = useState<ChartResponse | null>(null);
  const [rdrData, setRdrData] = useState<ChartResponse | null>(null);
  const [hasEthocaData, setHasEthocaData] = useState<boolean | null>(null);
  const [hasCdrnData, setHasCdrnData] = useState<boolean | null>(null);
  const [hasRdrData, setHasRdrData] = useState<boolean | null>(null);
  const theme = useTheme();

  const fetchChartData = useCallback(async () => {
    if (!startDate || !endDate) return;

    const params = new URLSearchParams({
      startDate,
      endDate,
      currency,
      ...(companyId && { companyId: companyId.toString() }),
      ...(brandId && { brandId: brandId.toString() }),
    });

    const [ethocaResult, cdrnResult, rdrResult] = await Promise.allSettled([
      get<ChartResponse>(`/api/alerts/ethoca/chart?${params.toString()}`),
      get<ChartResponse>(`/api/alerts/cdrn/chart?${params.toString()}`),
      get<ChartResponse>(`/api/alerts/rdr/chart?${params.toString()}`),
    ]);

    if (ethocaResult.status === "fulfilled") {
      setEthocaData(ethocaResult.value);
      setHasEthocaData(ethocaResult.value.summary.total > 0);
    } else {
      console.error("Failed to fetch Ethoca chart data:", ethocaResult.reason);
      setHasEthocaData(false);
    }

    if (cdrnResult.status === "fulfilled") {
      setCdrnData(cdrnResult.value);
      setHasCdrnData(cdrnResult.value.summary.total > 0);
    } else {
      console.error("Failed to fetch CDRN chart data:", cdrnResult.reason);
      setHasCdrnData(false);
    }

    if (rdrResult.status === "fulfilled") {
      setRdrData(rdrResult.value);
      setHasRdrData(rdrResult.value.summary.total > 0);
    } else {
      console.error("Failed to fetch RDR chart data:", rdrResult.reason);
      setHasRdrData(false);
    }
  }, [startDate, endDate, companyId, brandId, currency, get]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  // Show loading skeleton while fetching
  if (loading && hasEthocaData === null && hasCdrnData === null && hasRdrData === null) {
    return (
      <>
        {showTitle && (
          <h3 className="mt-8 mb-4 text-lg font-semibold text-gray-800 dark:text-white">
            Alerts
          </h3>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6">
          {[...Array(2)].map((_, index) => (
            <div key={index} className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded dark:bg-gray-700 w-20"></div>
                <div className="h-6 bg-gray-200 rounded dark:bg-gray-700 w-16 mt-2"></div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  // Hide when not on "overall" tab (component stays mounted to preserve state)
  if (activeTab !== "overall") {
    return null;
  }

  // Carry the dashboard's date window to the alert list page (which reads startDate/endDate);
  // companyId is preserved by the CmLink inside the card. Ethoca/CDRN default their table to
  // cm-status ACTION_NEEDED, so pass tab=all to show every alert — matching the chart's total.
  const alertsLink = (path: string, extra: Record<string, string> = {}) => {
    const p = new URLSearchParams();
    if (startDate) p.set("startDate", startDate);
    if (endDate) p.set("endDate", endDate);
    for (const [k, v] of Object.entries(extra)) p.set(k, v);
    const qs = p.toString();
    return qs ? `${path}?${qs}` : path;
  };

  // One shared y-axis ceiling across all three charts so a bar's height means the same count in
  // each — otherwise each auto-scales and the three aren't visually comparable.
  const allDailyValues = [ethocaData, cdrnData, rdrData].flatMap(
    (d) => d?.chart.series.flatMap((s) => s.data) ?? [],
  );
  const sharedYMax = niceCeil(allDailyValues.length ? Math.max(...allDailyValues) : 0);

  return (
    <>
      {showTitle && (
        <h3 className="mt-8 mb-4 text-lg font-semibold text-gray-800 dark:text-white">
          Alerts
        </h3>
      )}

      <Box sx={{ display: "flex", gap: 3, flexWrap: { xs: "wrap", md: "nowrap" }, alignItems: "stretch" }}>
        <AlertsChartCard
          title="Ethoca Alerts"
          href={alertsLink("/alerts/ethoca", { tab: "all" })}
          chart={ethocaData?.chart ?? null}
          loading={loading}
          theme={theme}
          total={ethocaData?.summary.total ?? null}
          previousTotal={ethocaData?.summary.previousTotal ?? null}
          yMax={sharedYMax}
          summary={[
            { label: "Total", value: ethocaData ? ethocaData.summary.total.toLocaleString() : "—" },
            { label: "Avg / Day", value: ethocaData ? ethocaData.summary.avgPerDay.toFixed(1) : "—" },
            { label: "Refunded", value: ethocaData ? formatCurrencyAdaptive(ethocaData.summary.totalRefundedAmount, currency) : "—" },
          ]}
        />
        <AlertsChartCard
          title="Verifi CDRN Alerts"
          href={alertsLink("/alerts/cdrn", { tab: "all" })}
          chart={cdrnData?.chart ?? null}
          loading={loading}
          theme={theme}
          total={cdrnData?.summary.total ?? null}
          previousTotal={cdrnData?.summary.previousTotal ?? null}
          yMax={sharedYMax}
          summary={[
            { label: "Total", value: cdrnData ? cdrnData.summary.total.toLocaleString() : "—" },
            { label: "Avg / Day", value: cdrnData ? cdrnData.summary.avgPerDay.toFixed(1) : "—" },
            { label: "Refunded", value: cdrnData ? formatCurrencyAdaptive(cdrnData.summary.totalRefundedAmount, currency) : "—" },
          ]}
        />
        <AlertsChartCard
          title="RDR Alerts"
          href={alertsLink("/alerts/rdr-notices")}
          chart={rdrData?.chart ?? null}
          loading={loading}
          theme={theme}
          total={rdrData?.summary.total ?? null}
          previousTotal={rdrData?.summary.previousTotal ?? null}
          yMax={sharedYMax}
          summary={[
            { label: "Total", value: rdrData ? rdrData.summary.total.toLocaleString() : "—" },
            { label: "Avg / Day", value: rdrData ? rdrData.summary.avgPerDay.toFixed(1) : "—" },
            { label: "Refunded", value: rdrData ? formatCurrencyAdaptive(rdrData.summary.totalRefundedAmount, currency) : "—" },
          ]}
        />
      </Box>
    </>
  );
}

interface AlertsChartCardProps {
  title: string;
  href: string;
  chart: ChartPayload | null;
  loading: boolean;
  theme: Theme;
  total: number | null;
  previousTotal: number | null;
  yMax?: number;
  summary: Array<{ label: string; value: string }>;
}

// Rounds a raw max up to a "nice" axis ceiling (e.g. 1750 → 2000) so the shared y-axis lands on
// a clean gridline. Returns undefined for non-positive input → MUI falls back to auto-scaling.
function niceCeil(n: number): number | undefined {
  if (n <= 0) return undefined;
  const magnitude = Math.pow(10, Math.floor(Math.log10(n)));
  const step = magnitude / 2;
  return Math.ceil(n / step) * step;
}

// Period-over-period change shown as a chip beside the Total — same visual language as the
// dashboard KPI cards. Treated as positive inflow (green up / red down), consistent with how
// "Chargebacks Received" is coloured. Hidden when there's no comparable prior period.
function TotalTrendChip({ total, previousTotal }: { total: number | null; previousTotal: number | null }) {
  if (total == null || previousTotal == null || previousTotal === 0) return null;

  const pct = ((total - previousTotal) / previousTotal) * 100;
  const rounded = Math.round(Math.abs(pct));
  if (rounded === 0) return null;

  const up = pct > 0;
  return (
    <Badge color={up ? "success" : "error"} startIcon={up ? <ArrowUpIcon /> : <ArrowDownIcon />}>
      {rounded}%
    </Badge>
  );
}

function AlertsChartCard({ title, href, chart, loading, theme, total, previousTotal, yMax, summary }: AlertsChartCardProps) {
  return (
    <ChartContainer
      title={title}
      loading={loading}
      href={href}
      action={
        // Decorative chevron — the whole card is the link (matches the dashboard StatCards).
        <span className="shrink-0 text-gray-300" aria-hidden>
          <svg className="size-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M7 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      }
    >
      {/* Fixed-height wrapping strip: the chart always starts at the same Y across cards,
          even when the Total's trend chip bumps a stat onto a second row. */}
      <div className="mb-5 flex flex-wrap content-start gap-x-8 gap-y-3 min-h-[6.5rem]">
        {summary.map((s) => (
          <div key={s.label}>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-400">{s.label}</div>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="text-xl font-bold text-gray-800 dark:text-white/90">{s.value}</span>
              {s.label === "Total" && <TotalTrendChip total={total} previousTotal={previousTotal} />}
            </div>
          </div>
        ))}
      </div>
      {chart && chart.xAxis.length > 0 && chart.series.some((s) => s.data.some((v) => v > 0)) ? (
        <BarChart
          xAxis={[
            {
              data: chart.xAxis,
              valueFormatter: (value: string, ctx: AxisValueFormatterContext) => formatAxisDate(value, ctx.location),
              tickLabelStyle: { fontSize: 11, fill: theme.palette.text.secondary },
              disableTicks: true,
            },
          ]}
          // Whole-number y-axis so alert counts are readable off the chart. tickMinStep: 1 avoids
          // fractional ticks (0.5, 1.5…) on low-volume days; min: 0 anchors the baseline at zero.
          // Line + ticks dropped so labels read against the horizontal gridlines (less chart-junk).
          yAxis={[
            {
              min: 0,
              max: yMax,
              tickMinStep: 1,
              width: 36,
              valueFormatter: (value: number | null) => (value == null ? "" : value.toLocaleString()),
              tickLabelStyle: { fontSize: 11, fill: theme.palette.text.secondary },
              disableLine: true,
              disableTicks: true,
            },
          ]}
          // Horizontal gridlines let you read a bar's magnitude at a glance without hovering each one.
          grid={{ horizontal: true }}
          series={chart.series.map((s) => ({
            data: s.data,
            label: s.label,
            color: theme.palette.primary.main,
          }))}
          height={200}
          margin={{ left: 0, right: 4, top: 8, bottom: 0 }}
          borderRadius={3}
          hideLegend
        />
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, minHeight: 220 }}>
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      )}
    </ChartContainer>
  );
}
